import { useCallback, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  useGetEtlAnomalyChecksQuery,
  useGetEtlDailyRollupQuery,
  useGetEtlMartVerificationQuery,
  useGetEtlPlatformReconciliationQuery,
} from 'src/redux/operations/operations.endpoints';
import { AnomalyChecksPanel } from './anomaly-checks-panel';
import { EtlHealthStrip } from './etl-health-strip';
import { MartVerificationPanel } from './mart-verification-panel';
import { PlatformReconciliationPanel } from './platform-reconciliation-panel';

const HEALTH_DAYS = 30;

function getErrorMessage(error: unknown): string {
  return (error as { message?: string })?.message ?? 'An unexpected error occurred.';
}

export function EtlHealthDashboard() {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(false);

  const queryParams = { days: HEALTH_DAYS, ...(refresh ? { refresh: 1 as const } : {}) };

  const rollupQuery = useGetEtlDailyRollupQuery(queryParams);
  const reconciliationQuery = useGetEtlPlatformReconciliationQuery(queryParams);
  const anomalyQuery = useGetEtlAnomalyChecksQuery(queryParams);
  const martQuery = useGetEtlMartVerificationQuery(queryParams);

  const isLoading = rollupQuery.isLoading
    || reconciliationQuery.isLoading
    || anomalyQuery.isLoading
    || martQuery.isLoading;

  const isFetching = rollupQuery.isFetching
    || reconciliationQuery.isFetching
    || anomalyQuery.isFetching
    || martQuery.isFetching;

  const isError = rollupQuery.isError
    || reconciliationQuery.isError
    || anomalyQuery.isError
    || martQuery.isError;

  const error = rollupQuery.error
    ?? reconciliationQuery.error
    ?? anomalyQuery.error
    ?? martQuery.error;

  const handleRefresh = useCallback(async () => {
    setRefresh(true);
    try {
      await Promise.all([
        rollupQuery.refetch(),
        reconciliationQuery.refetch(),
        anomalyQuery.refetch(),
        martQuery.refetch(),
      ]);
    } finally {
      setRefresh(false);
    }
  }, [anomalyQuery, martQuery, reconciliationQuery, rollupQuery]);

  return (
    <div className="flex flex-col gap-6">
      <section
        className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
        aria-labelledby="etl-health-heading"
      >
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id="etl-health-heading" className="text-base font-semibold text-gray-900">
              ETL health
            </h2>
            <p className="text-sm text-gray-600">
              Last
              {' '}
              {HEALTH_DAYS}
              {' '}
              days of data quality checks across reconciliation, anomaly detection, and mart verification.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {selectedDate ? (
              <button
                type="button"
                className="text-sm text-gray-600 hover:text-gray-900"
                onClick={() => setSelectedDate(null)}
              >
                Clear day filter (
                {selectedDate}
                )
              </button>
            ) : null}
            <button
              type="button"
              className="text-sm font-medium text-violet-700 hover:text-violet-900 disabled:opacity-50"
              onClick={() => void handleRefresh()}
              disabled={isFetching}
            >
              Refresh
            </button>
          </div>
        </div>

        {(isLoading || isFetching) && (
          <div className="mb-4 flex items-center gap-2 text-sm text-gray-600" role="status">
            <Loader2 className="size-4 animate-spin" />
            Loading ETL health data…
          </div>
        )}

        {isError && !isLoading && (
          <div
            className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
            role="alert"
          >
            <p className="font-medium">Could not load ETL health dashboard</p>
            <p className="mt-1 text-red-700">{getErrorMessage(error)}</p>
          </div>
        )}

        {!isLoading && !isError && rollupQuery.data?.rollup?.length ? (
          <EtlHealthStrip
            days={rollupQuery.data.rollup}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
          />
        ) : null}
      </section>

      {!isLoading && !isError && (
        <>
          <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-900">Platform reconciliation</h3>
              <p className="text-sm text-gray-600">
                Vendor vs mart totals by platform, account, and metric.
              </p>
            </div>
            <PlatformReconciliationPanel
              rows={reconciliationQuery.data?.rows ?? []}
              selectedDate={selectedDate}
            />
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-900">Anomaly checks</h3>
              <p className="text-sm text-gray-600">
                Statistical anomaly detection results by platform and check type.
              </p>
            </div>
            <AnomalyChecksPanel
              rows={anomalyQuery.data?.rows ?? []}
              selectedDate={selectedDate}
            />
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-900">Mart verification</h3>
              <p className="text-sm text-gray-600">
                Mart-level verification checks by datasource and connector.
              </p>
            </div>
            <MartVerificationPanel
              rows={martQuery.data?.rows ?? []}
              selectedDate={selectedDate}
            />
          </section>
        </>
      )}
    </div>
  );
}
